import { NgTemplateOutlet } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
    ChangeDetectionStrategy,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import {
    HippoNavigationService,
    HippoVerticalNavigationComponent,
} from '@hippo/components/navigation';
import { LangDefinition, TranslocoService } from '@jsverse/transloco';
import { SharedService } from 'app/core/services/shared.service';
import { UserService } from 'app/core/services/user.service';
import { MyDataService } from 'app/modules/admin/team/my-data/my-data.service';
import { UserData } from 'app/modules/admin/team/my-data/my-data.types';
import { lastValueFrom, Subscription, take } from 'rxjs';

@Component({
    selector: 'languages',
    templateUrl: './languages.component.html',
    encapsulation: ViewEncapsulation.None,
    // changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'languages',
    changeDetection: ChangeDetectionStrategy.Default,
    imports: [MatButtonModule, MatMenuModule, NgTemplateOutlet],
})
export class LanguagesComponent implements OnInit, OnDestroy {
    availableLangs!: LangDefinition[];
    activeLang!: string;
    flagCodes!: any;

    userData!: UserData;
    openedMenus!: boolean[];
    langChangeSub!: Subscription;

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _hippoNavigationService: HippoNavigationService,
        private _translocoService: TranslocoService,
        private httpClient: HttpClient,
        private userService: UserService,
        private sharedService: SharedService,
        private myDataService: MyDataService
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Get the available languages from transloco
        this.availableLangs =
            this._translocoService.getAvailableLangs() as LangDefinition[];

        // *** Workaround - English language isn`t loaded by default
        // not part of orgiginal fuse template
        this._translocoService.load('en').subscribe();
        // ***

        // Subscribe to language changes
        this.langChangeSub = this._translocoService.langChanges$.subscribe(
            activeLang => {
                // Get the active lang
                this.activeLang = activeLang;

                // Update the navigation
                this._updateNavigation(activeLang);
            }
        );

        // Set the country iso codes for languages for flags
        this.flagCodes = {
            en: 'us',
            de: 'de',
        };
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        if (this.langChangeSub) {
            this.langChangeSub.unsubscribe();
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Set the active lang
     *
     * @param lang
     */
    async setActiveLang(lang: string): Promise<void> {
        // Switch the UI first. The profile save below needs a logged-in user
        // (it posts to User_data.php with the uid), so on the sign-in page —
        // where this component is shown too — it throws and used to leave the
        // app stuck in the default language. That also pinned the Longines-ID /
        // Cognito hosted UI to German, since it is opened with the active
        // language (HIP-43).
        this._translocoService.setActiveLang(lang);

        // Nobody to persist the preference for yet.
        if (!this.userService.getUser()?.uid) {
            return;
        }

        await this.loadUserData(lang);
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Update the navigation
     *
     * @param lang
     * @private
     */
    private _updateNavigation(lang: string): void {
        // For the demonstration purposes, we will only update the Dashboard names
        // from the navigation but you can do a full swap and change the entire
        // navigation data.
        //
        // You can import the data from a file or request it from your backend,
        // it's up to you.

        // Get the component -> navigation data -> item
        const navComponent =
            this._hippoNavigationService.getComponent<HippoVerticalNavigationComponent>(
                'mainNavigation'
            );

        // Return if the navigation component does not exist
        if (!navComponent) {
            return;
        }

        // Get the flat navigation data
        const navigation = navComponent.navigation;

        // Get the Project dashboard item and update its title
        const projectDashboardItem = this._hippoNavigationService.getItem(
            'dashboards.project',
            navigation
        );
        if (projectDashboardItem) {
            this._translocoService
                .selectTranslate('Project')
                .pipe(take(1))
                .subscribe(translation => {
                    // Set the title
                    projectDashboardItem.title = translation;

                    // Refresh the navigation component
                    navComponent.refresh();
                });
        }

        // Get the Analytics dashboard item and update its title
        const analyticsDashboardItem = this._hippoNavigationService.getItem(
            'dashboards.analytics',
            navigation
        );
        if (analyticsDashboardItem) {
            this._translocoService
                .selectTranslate('Analytics')
                .pipe(take(1))
                .subscribe(translation => {
                    // Set the title
                    analyticsDashboardItem.title = translation;

                    // Refresh the navigation component
                    navComponent.refresh();
                });
        }
    }

    async loadUserData(lang: string) {
        try {
            const formData = new FormData();
            formData.append('submit_option', 'opt_reset');
            const data = await lastValueFrom(
                this.httpClient.post<any>(
                    `hippo/navigator.php?page=pages/users/User_data.php&uid=${this.userService.user.uid}&page_type=json`,
                    formData,
                    {
                        observe: 'response',
                    }
                )
            );
            this.userData = JSON.parse(JSON.stringify(data.body.result));
            this.openedMenus = Array(13).fill(false);
            let userSettingsMenu = this.userData.user_settings_menu;
            if (userSettingsMenu) {
                const userMenuSettingsSplitted = userSettingsMenu.split(';');
                userMenuSettingsSplitted.forEach(menu => {
                    this.openedMenus[+menu] = true;
                });
            }
            await this.saveUserData(lang);
            this.myDataService.userData.user_settings_language = lang;
            this.myDataService.originalUserData.user_settings_language = lang;
        } catch (error) {
            console.log(error);
            this.sharedService.showDataNotSaved(
                error,
                'LanguagesComponent',
                'loadUserData'
            );
        }
    }

    async saveUserData(lang: string) {
        const formData = new FormData();
        formData.append('submit_option', 'opt_save');
        formData.append('usr_salutation', this.userData.usr_salutation ?? '');
        formData.append('usr_first_name', this.userData.usr_first_name ?? '');
        formData.append('usr_last_name', this.userData.usr_last_name ?? '');
        formData.append('usr_e_mail', this.userData.usr_e_mail ?? '');
        formData.append('usr_adr_id', this.userData.usr_adr_id ?? '');
        formData.append('usr_street', this.userData.usr_street ?? '');
        formData.append('usr_zip_code', this.userData.usr_zip_code ?? '');
        formData.append('usr_city', this.userData.usr_city ?? '');
        formData.append('usr_country', this.userData.usr_country ?? '');
        formData.append(
            'usr_phone_number',
            this.userData.usr_phone_number ?? ''
        );
        formData.append(
            'usr_mobile_number',
            this.userData.usr_mobile_number ?? ''
        );
        formData.append('usr_fax_number', this.userData.usr_fax_number ?? '');
        formData.append('usr_birthday', this.userData.usr_birthday ?? '');
        formData.append('usr_entry_date', this.userData.usr_entry_date ?? '');

        formData.append('uad_id', this.userData.uad_id ?? '');
        formData.append('uad_shirt_size', this.userData.uad_shirt_size ?? '');
        formData.append('uad_correct_fit', this.userData.uad_correct_fit ?? '');
        formData.append(
            'uad_sleeve_length',
            this.userData.uad_sleeve_length ?? ''
        );
        formData.append(
            'uad_polo_shirt_size',
            this.userData.uad_polo_shirt_size ?? ''
        );
        formData.append(
            'uad_polo_shirt_quantity_required',
            this.userData.uad_polo_shirt_quantity_required ?? ''
        );
        formData.append(
            'uad_polo_shirt_quantity_owned',
            this.userData.uad_polo_shirt_quantity_owned ?? ''
        );
        formData.append('uad_has_watch', this.userData.uad_has_watch ?? '');
        formData.append('uad_watch_model', this.userData.uad_watch_model ?? '');

        formData.append('udl_id', this.userData.udl_id ?? '');
        formData.append(
            'udl_drivers_licence_number',
            this.userData.udl_drivers_licence_number ?? ''
        );
        formData.append(
            'udl_has_licence_b',
            this.userData.udl_has_licence_b ?? ''
        );
        formData.append(
            'udl_has_licence_be',
            this.userData.udl_has_licence_be ?? ''
        );
        formData.append(
            'udl_has_licence_c1',
            this.userData.udl_has_licence_c1 ?? ''
        );
        formData.append(
            'udl_licence_c1_date_to',
            this.userData.udl_licence_c1_date_to ?? ''
        );
        formData.append(
            'udl_licence_c1_restrictions',
            this.userData.udl_licence_c1_restrictions ?? ''
        );
        formData.append(
            'udl_has_licence_c1e',
            this.userData.udl_has_licence_c1e ?? ''
        );
        formData.append(
            'udl_licence_c1e_date_to',
            this.userData.udl_licence_c1e_date_to ?? ''
        );
        formData.append(
            'udl_licence_c1e_restrictions',
            this.userData.udl_licence_c1e_restrictions ?? ''
        );
        formData.append(
            'udl_has_licence_c',
            this.userData.udl_has_licence_c ?? ''
        );
        formData.append(
            'udl_licence_c_date_to',
            this.userData.udl_licence_c_date_to ?? ''
        );
        formData.append(
            'udl_licence_c_restrictions',
            this.userData.udl_licence_c_restrictions ?? ''
        );
        formData.append(
            'udl_has_licence_ce',
            this.userData.udl_has_licence_ce ?? ''
        );
        formData.append(
            'udl_licence_ce_date_to',
            this.userData.udl_licence_ce_date_to ?? ''
        );
        formData.append(
            'udl_licence_ce_restrictions',
            this.userData.udl_licence_ce_restrictions ?? ''
        );
        formData.append(
            'udl_has_driver_card',
            this.userData.udl_has_driver_card ?? ''
        );
        formData.append(
            'udl_driver_card_number',
            this.userData.udl_driver_card_number ?? ''
        );
        formData.append(
            'udl_driver_card_date_to',
            this.userData.udl_driver_card_date_to ?? ''
        );
        formData.append(
            'udl_has_driver_qualification_card',
            this.userData.udl_has_driver_qualification_card ?? ''
        );
        formData.append(
            'udl_driver_qualification_card_number',
            this.userData.udl_driver_qualification_card_number ?? ''
        );
        formData.append(
            'udl_driver_qualification_card_date_to',
            this.userData.udl_driver_qualification_card_date_to ?? ''
        );

        formData.append(
            'uad_global_entry_usa',
            this.userData.uad_global_entry_usa ?? ''
        );

        formData.append(
            '0_upd_id',
            this.userData.usr_passport[0]?.upd_id ?? ''
        );
        formData.append(
            '0_upd_number',
            this.userData.usr_passport[0]?.upd_number ?? ''
        );
        formData.append(
            '0_upd_last_name',
            this.userData.usr_passport[0]?.upd_last_name ?? ''
        );
        formData.append(
            '0_upd_first_name',
            this.userData.usr_passport[0]?.upd_first_name ?? ''
        );
        formData.append(
            '0_upd_nationality',
            this.userData.usr_passport[0]?.upd_nationality ?? ''
        );
        formData.append(
            '0_upd_sex',
            this.userData.usr_passport[0]?.upd_sex ?? ''
        );
        formData.append(
            '0_upd_birth_place',
            this.userData.usr_passport[0]?.upd_birth_place ?? ''
        );
        formData.append(
            '0_upd_issue_place',
            this.userData.usr_passport[0]?.upd_issue_place ?? ''
        );
        formData.append(
            '0_upd_issue_date',
            this.userData.usr_passport[0]?.upd_issue_date ?? ''
        );
        formData.append(
            '0_upd_valid_to_date',
            this.userData.usr_passport[0]?.upd_valid_to_date ?? ''
        );
        formData.append(
            '1_upd_id',
            this.userData.usr_passport[1]?.upd_id ?? ''
        );
        formData.append(
            '1_upd_number',
            this.userData.usr_passport[1]?.upd_number ?? ''
        );
        formData.append(
            '1_upd_last_name',
            this.userData.usr_passport[1]?.upd_last_name ?? ''
        );
        formData.append(
            '1_upd_first_name',
            this.userData.usr_passport[1]?.upd_first_name ?? ''
        );
        formData.append(
            '1_upd_nationality',
            this.userData.usr_passport[1]?.upd_nationality ?? ''
        );
        formData.append(
            '1_upd_sex',
            this.userData.usr_passport[1]?.upd_sex ?? ''
        );
        formData.append(
            '1_upd_birth_place',
            this.userData.usr_passport[1]?.upd_birth_place ?? ''
        );
        formData.append(
            '1_upd_issue_place',
            this.userData.usr_passport[1]?.upd_issue_place ?? ''
        );
        formData.append(
            '1_upd_issue_date',
            this.userData.usr_passport[1]?.upd_issue_date ?? ''
        );
        formData.append(
            '1_upd_valid_to_date',
            this.userData.usr_passport[1]?.upd_valid_to_date ?? ''
        );
        formData.append(
            'upc_id_1_0',
            this.userData.travel_cards[1]?.[0]?.upc_id ?? ''
        );
        formData.append(
            'ucp_id_1_0',
            this.userData.travel_cards[1]?.[0]?.ucp_id ?? ''
        );
        formData.append(
            'upc_number_1_0',
            this.userData.travel_cards[1]?.[0]?.upc_number ?? ''
        );
        formData.append(
            'upc_id_1_1',
            this.userData.travel_cards[1]?.[1]?.upc_id ?? ''
        );
        formData.append(
            'ucp_id_1_1',
            this.userData.travel_cards[1]?.[1]?.ucp_id ?? ''
        );
        formData.append(
            'upc_number_1_1',
            this.userData.travel_cards[1]?.[1]?.upc_number ?? ''
        );
        formData.append(
            'upc_id_1_2',
            this.userData.travel_cards[1]?.[2]?.upc_id ?? ''
        );
        formData.append(
            'ucp_id_1_2',
            this.userData.travel_cards[1]?.[2]?.ucp_id ?? ''
        );
        formData.append(
            'upc_number_1_2',
            this.userData.travel_cards[1]?.[2]?.upc_number ?? ''
        );
        formData.append(
            'upc_id_1_3',
            this.userData.travel_cards[1]?.[3]?.upc_id ?? ''
        );
        formData.append(
            'ucp_id_1_3',
            this.userData.travel_cards[1]?.[3]?.ucp_id ?? ''
        );
        formData.append(
            'upc_number_1_3',
            this.userData.travel_cards[1]?.[3]?.upc_number ?? ''
        );
        formData.append(
            'upc_id_1_4',
            this.userData.travel_cards[1]?.[4]?.upc_id ?? ''
        );
        formData.append(
            'ucp_id_1_4',
            this.userData.travel_cards[1]?.[4]?.ucp_id ?? ''
        );
        formData.append(
            'upc_number_1_4',
            this.userData.travel_cards[1]?.[4]?.upc_number ?? ''
        );
        formData.append(
            'upc_id_2_0',
            this.userData.travel_cards[2]?.[0]?.upc_id ?? ''
        );
        formData.append(
            'ucp_id_2_0',
            this.userData.travel_cards[2]?.[0]?.ucp_id ?? ''
        );
        formData.append(
            'upc_number_2_0',
            this.userData.travel_cards[2]?.[0]?.upc_number ?? ''
        );
        formData.append(
            'upc_id_2_1',
            this.userData.travel_cards[2]?.[1]?.upc_id ?? ''
        );
        formData.append(
            'ucp_id_2_1',
            this.userData.travel_cards[2]?.[1]?.ucp_id ?? ''
        );
        formData.append(
            'upc_number_2_1',
            this.userData.travel_cards[2]?.[1]?.upc_number ?? ''
        );
        formData.append(
            'upc_id_2_2',
            this.userData.travel_cards[2]?.[2]?.upc_id ?? ''
        );
        formData.append(
            'ucp_id_2_2',
            this.userData.travel_cards[2]?.[2]?.ucp_id ?? ''
        );
        formData.append(
            'upc_number_2_2',
            this.userData.travel_cards[2]?.[2]?.upc_number ?? ''
        );
        formData.append(
            'upc_id_2_3',
            this.userData.travel_cards[2]?.[3]?.upc_id ?? ''
        );
        formData.append(
            'ucp_id_2_3',
            this.userData.travel_cards[2]?.[3]?.ucp_id ?? ''
        );
        formData.append(
            'upc_number_2_3',
            this.userData.travel_cards[2]?.[3]?.upc_number ?? ''
        );
        formData.append(
            'upc_id_3_0',
            this.userData.travel_cards[3]?.[0]?.upc_id ?? ''
        );
        formData.append(
            'ucp_id_3_0',
            this.userData.travel_cards[3]?.[0]?.ucp_id ?? ''
        );
        formData.append(
            'upc_number_3_0',
            this.userData.travel_cards[3]?.[0]?.upc_number ?? ''
        );
        formData.append(
            'upc_id_3_1',
            this.userData.travel_cards[3]?.[1]?.upc_id ?? ''
        );
        formData.append(
            'ucp_id_3_1',
            this.userData.travel_cards[3]?.[1]?.ucp_id ?? ''
        );
        formData.append(
            'upc_number_3_1',
            this.userData.travel_cards[3]?.[1]?.upc_number ?? ''
        );
        formData.append(
            'upc_id_3_2',
            this.userData.travel_cards[3]?.[2]?.upc_id ?? ''
        );
        formData.append(
            'ucp_id_3_2',
            this.userData.travel_cards[3]?.[2]?.ucp_id ?? ''
        );
        formData.append(
            'upc_number_3_2',
            this.userData.travel_cards[3]?.[2]?.upc_number ?? ''
        );
        formData.append(
            'upc_id_3_3',
            this.userData.travel_cards[3]?.[3]?.upc_id ?? ''
        );
        formData.append(
            'ucp_id_3_3',
            this.userData.travel_cards[3]?.[3]?.ucp_id ?? ''
        );
        formData.append(
            'upc_number_3_3',
            this.userData.travel_cards[3]?.[3]?.upc_number ?? ''
        );
        formData.append(
            'upc_id_3_4',
            this.userData.travel_cards[3]?.[4]?.upc_id ?? ''
        );
        formData.append(
            'ucp_id_3_4',
            this.userData.travel_cards[3]?.[4]?.ucp_id ?? ''
        );
        formData.append(
            'upc_number_3_4',
            this.userData.travel_cards[3]?.[4]?.upc_number ?? ''
        );
        formData.append(
            '0_utc_id',
            this.userData.usr_train_card[0]?.utc_id ?? ''
        );
        formData.append(
            '0_utc_company',
            this.userData.usr_train_card[0]?.utc_company ?? ''
        );
        formData.append(
            '0_utc_card_type',
            this.userData.usr_train_card[0]?.utc_card_type ?? ''
        );
        formData.append(
            '0_utc_number',
            this.userData.usr_train_card[0]?.utc_number ?? ''
        );
        formData.append(
            '0_utc_date_to',
            this.userData.usr_train_card[0]?.utc_date_to ?? ''
        );

        formData.append('uad_flight_seat', this.userData.uad_flight_seat ?? '');
        formData.append('uad_flight_meal', this.userData.uad_flight_meal ?? '');
        formData.append(
            'uad_flight_home_airport_1',
            this.userData.uad_flight_home_airport_1 ?? ''
        );
        formData.append(
            'uad_flight_home_airport_2',
            this.userData.uad_flight_home_airport_2 ?? ''
        );
        formData.append(
            'uad_flight_home_airport_3',
            this.userData.uad_flight_home_airport_3 ?? ''
        );
        formData.append(
            'uad_flight_alternative_airport_1',
            this.userData.uad_flight_alternative_airport_1 ?? ''
        );
        formData.append(
            'uad_flight_alternative_airport_2',
            this.userData.uad_flight_alternative_airport_2 ?? ''
        );
        formData.append(
            'uad_flight_alternative_airport_3',
            this.userData.uad_flight_alternative_airport_3 ?? ''
        );

        this.userData.usr_hyperlinks.forEach(hyperlink => {
            formData.append('txt_name_' + hyperlink.hlk_id, hyperlink.hlk_name);
            formData.append('txt_link_' + hyperlink.hlk_id, hyperlink.hlk_link);
            formData.append(
                'txt_remarks_' + hyperlink.hlk_id,
                hyperlink.hlk_remarks
            );
        });
        formData.append(
            'submit_link_ids',
            this.userData.usr_hyperlinks
                .map(hyperlink => hyperlink.hlk_id)
                .toString()
                .replaceAll(',', ';')
        );

        formData.append('user_settings_language', lang);
        formData.append(
            'user_settings_color',
            this.userData.user_settings_color
        );
        formData.append('submit_active_menu', '0;1;2;3;4;5;6;7;8;9;10;11;12');
        formData.append(
            'uss_menu_0',
            (+(this.openedMenus[0] ?? false)).toString()
        );
        formData.append(
            'uss_menu_1',
            (+(this.openedMenus[1] ?? false)).toString()
        );
        formData.append(
            'uss_menu_2',
            (+(this.openedMenus[2] ?? false)).toString()
        );
        formData.append(
            'uss_menu_3',
            (+(this.openedMenus[3] ?? false)).toString()
        );
        formData.append(
            'uss_menu_4',
            (+(this.openedMenus[4] ?? false)).toString()
        );
        formData.append(
            'uss_menu_5',
            (+(this.openedMenus[5] ?? false)).toString()
        );
        formData.append(
            'uss_menu_6',
            (+(this.openedMenus[6] ?? false)).toString()
        );
        formData.append(
            'uss_menu_7',
            (+(this.openedMenus[7] ?? false)).toString()
        );
        formData.append(
            'uss_menu_8',
            (+(this.openedMenus[8] ?? false)).toString()
        );
        formData.append(
            'uss_menu_9',
            (+(this.openedMenus[9] ?? false)).toString()
        );
        formData.append(
            'uss_menu_10',
            (+(this.openedMenus[10] ?? false)).toString()
        );
        formData.append(
            'uss_menu_11',
            (+(this.openedMenus[11] ?? false)).toString()
        );
        formData.append(
            'uss_menu_12',
            (+(this.openedMenus[12] ?? false)).toString()
        );
        // formData.append('uad_blouse_size', this.userData.uad_blouse_size);

        try {
            await lastValueFrom(
                this.httpClient.post(
                    `hippo/navigator.php?page=pages/users/User_data.php&uid=${this.userService.user.uid}&page_type=json`,
                    formData,
                    {
                        observe: 'response',
                    }
                )
            );
            this.sharedService.apiSystemLanguage =
                this.userData.user_settings_language;
        } catch (error) {
            console.log(error);
            this.sharedService.showDataNotSaved(
                error,
                'LanguagesComponent',
                'saveUserData'
            );
        }
    }
}
